<template>
  <div>
    <h1 class="section-divider-underline">{{ $t('employee.detail') }}</h1>
    <div class="row">
      <div class="col-md-6">
        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4">{{ $t('employee.dob') }}</label>
          <div class="col-md-7">
            <ts-date-picker-input v-model="model.date_of_birth"></ts-date-picker-input>
          </div>
        </div>

        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4">{{ $t('employee.nationality') }}</label>
          <div class="col-md-7">
            <select
              v-model.number="model.nationality_id"
              class="form-control"
              :class="{ 'is-invalid': errors.has('nationality_id') }"
            >
              <option value>{{ $t('select') }}</option>
              <option
                v-for="(nationality, index) in nationalities"
                :key="index"
                :value="nationality.nationality_id"
              >{{ nationality.nationality_name_en }}</option>
            </select>
            <div
              class="invalid-feedback"
              v-if="errors.has('nationality_id')"
            >{{ errors.first('nationality_id') }}</div>
          </div>
        </div>

        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4 required">
            {{ $t('employee.status') }}
            <i
              class="fas fa-spinner spin"
              v-if="!status.length && ready_fetch"
            ></i>
          </label>
          <div class="col-md-7">
            <select
              v-model.number="model.status_id"
              class="form-control"
              :class="{ 'is-invalid': errors.has('status_id') }"
              @change="clearOption"
            >
              <option value disabled selected style="display: none;">{{ $t('select') }}</option>
              <option v-for="(st, index) in status" :key="index" :value="st.status_id">
                {{
                st.status
                }}
              </option>
            </select>
            <div
              class="invalid-feedback"
              v-if="errors.has('status_id')"
            >{{ errors.first('status_id') }}</div>
          </div>
        </div>

        <div class="row form-group m-b-15" v-if="model.status_id == 2 || model.status_id == 3">
          <label
            class="form-col-label control-label col-md-4"
            :class="{'required': model.status_id == 2 || model.status_id == 3 }"
          >
            {{
            $t('employee.statusEffectiveDate')
            }}
          </label>
          <div class="col-md-7">
            <ts-date-picker-input v-model="model.status_effective_date"></ts-date-picker-input>
            <div
              class="text-danger"
              v-if="errors.has('status_effective_date')"
            >{{ errors.first('status_effective_date') }}</div>
          </div>
        </div>

        <div class="row form-group m-b-15" v-if="model.status_id == 2 || model.status_id == 3">
          <label
            class="form-col-label control-label col-md-4"
            :class="{'required': model.status_id == 2 || model.status_id == 3 }"
          >
            {{
            $t('employee.statusReason')
            }}
          </label>
          <div class="col-md-7">
            <treeselect
              :options="options"
              :load-options="loadOptions"
              v-model="model.status_reason_id"
            />
            <div
              class="text-danger"
              v-if="errors.has('status_reason_id')"
            >{{ errors.first('status_reason_id') }}</div>
          </div>
        </div>

        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4">{{ $t('employee.phone') }}</label>
          <div class="col-md-7">
            <input
              type="text"
              v-model="model.phone"
              class="form-control"
              :class="{ 'is-invalid': errors.has('phone') }"
            />
            <div class="invalid-feedback" v-if="errors.has('phone')">{{ errors.first('phone') }}</div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4">{{ $t('employee.email') }}</label>
          <div class="col-md-7">
            <input
              type="text"
              v-model="model.email"
              class="form-control"
              :class="{ 'is-invalid': errors.has('email') }"
            />
            <div class="invalid-feedback" v-if="errors.has('email')">{{ errors.first('email') }}</div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4">{{ $t('employee.address') }}</label>
          <div class="col-md-7">
            <textarea
              rows="3"
              v-model="model.address"
              class="form-control"
              :class="{ 'is-invalid': errors.has('address') }"
            ></textarea>
            <div class="invalid-feedback" v-if="errors.has('address')">{{ errors.first('address') }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="row form-group m-b-15">
          <label
            class="form-col-label control-label col-md-4 required"
          >{{ $t('employee.hireDate') }}</label>
          <div class="col-md-7">
            <ts-date-picker-input v-model="model.hire_date" @input="addProbationDate"></ts-date-picker-input>
            <div class="text-danger" v-if="errors.has('hire_date')">{{ errors.first('hire_date') }}</div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4 required">
            {{
            $t('employee.probationEndDate')
            }}
          </label>
          <div class="col-md-7">
            <ts-date-picker-input v-model="model.probation_end_date" disabled="disabled"></ts-date-picker-input>
            <div
              class="text-danger"
              v-if="errors.has('probation_end_date')"
            >{{ errors.first('probation_end_date') }}</div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label
            class="form-col-label control-label col-md-4 required"
          >{{ $t('employee.employeeType') }}</label>
          <div class="col-md-7">
            <select
              v-model.number="model.emp_type_id"
              class="form-control"
              :class="{ 'is-invalid': errors.has('emp_type_id') }"
            >
              <option value disabled selected style="display: none;">{{ $t('select') }}</option>
              <option value="1">បុគ្គលិកក្នុងស្រុក Local</option>
              <option value="2">ជនបរទេស Expat</option>
              <option value="3">បុគ្គលិកឯករាជ្យ Freelancer</option>
            </select>
            <div
              class="invalid-feedback"
              v-if="errors.has('emp_type_id')"
            >{{ errors.first('emp_type_id') }}</div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="form-col-label control-label col-md-4 required">
            {{ $t('employee.shift') }}
            <i
              class="fas fa-spinner spin"
              v-if="!shifts.length && ready_fetch"
            ></i>
          </label>
          <div class="col-md-7">
            <select
              v-model="model.shift_id"
              class="form-control"
              :class="{ 'is-invalid': errors.has('shift_id') }"
            >
              <option value disabled selected style="display: none;">{{ $t('select') }}</option>
              <option
                v-for="(shift, index) in shifts"
                :key="index"
                :value="shift.shift_id"
              >{{ shift.shift_name }} ({{ shift.shift_type }})</option>
            </select>
            <div
              class="invalid-feedback"
              v-if="errors.has('shift_id')"
            >{{ errors.first('shift_id') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LOAD_ROOT_OPTIONS } from '@riophae/vue-treeselect'
import { Errors } from 'form-backend-validation'
import { mapState, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'detailTemplate',
  props: ['value', 'validation'],
  data() {
    return {
      model: {},
      errors: new Errors(),
      ready_fetch: false,
      options: null
    }
  },
  created() {
    this.model = this.value
    this.fetchBeforCreate()
  },
  computed: {
    ...mapState('hr/employee', ['status', 'shifts', 'nationalities', 'edit_data'])
  },
  methods: {
    ...mapActions('hr/employee', ['getStatusReason']),
    fetchBeforCreate() {
      this.ready_fetch = true
      this.$store
        .dispatch('hr/employee/dataBeforeCreate')
        .then(response => {
          this.ready_fetch = false
        })
        .catch(error => {
          this.ready_fetch = false
          this.$toasted.error(error.message)
        })
    },
    addProbationDate() {
      this.model.probation_end_date = moment(this.model.hire_date, 'DD-MM-YYYY')
        .add(3, 'M')
        .format('DD-MM-YYYY')
    },
    async loadOptions({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        let response = await this.getStatusReason({
          fields: {
            hris_employee_status_reasons: 'status_reason_id,reason'
          },
          filter: {
            status_id: this.model.status_id
          }
        })
        this.options = response.data.map(el => ({ id:el.status_reason_id, label: el.reason }))
      }
    },
    clearOption(){
      this.model.status_reason_id = null
      this.options = null
    }
  },
  watch: {
    'model.status_id': function(value) {
      if ((value == 2) | (value == 3)) {
        this.model.status_effective_date = !_.isEmpty(this.edit_data)
          ? this.edit_data.status_effective_date
          : moment().format('DD-MM-YYYY')

          this.model.status_reason_id = 
          !_.isEmpty(this.edit_data)
          ? this.edit_data.status_reason_id
          : null
      } else {
        this.model.status_effective_date = ''
      }
    },
    validation: {
      handler() {
        this.errors = new Errors(this.validation.errors)
      },
      deep: true
    }
  }
}
</script>

<style lang="css" scoped>
.section-divider-underline {
  padding-bottom: 5px;
  margin: 0 0 8px 0;
  /* border-bottom: 1px solid #eee; */
  font-size: 16px;
  color: #929090;
  font-weight: 400;
  text-transform: uppercase;
}
</style>